const MundoRelojeroSearchIntent = (() => {
  const automaticCollection = '/collections/automatic-watches';

  const normalize = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, ' and ')
      .replace(/['’]/g, '')
      .replace(/[-_/]+/g, ' ')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const stripWatchSuffix = (value) => value.replace(/\s+watch(?:es)?$/, '').trim();

  const brandCollections = new Map([
    ['anne klein', { url: '/collections/anne-klein-watches', vendor: 'Anne Klein' }],
    ['casio', { url: '/collections/casio-watches', vendor: 'Casio' }],
    ['certina', { url: '/collections/certina-watches', vendor: 'Certina' }],
    ['citizen', { url: '/collections/citizen-watches', vendor: 'Citizen' }],
    ['fossil', { url: '/collections/fossil-watches', vendor: 'Fossil' }],
    ['invicta', { url: '/collections/invicta-watches', vendor: 'Invicta' }],
    ['michael kors', { url: '/collections/michael-kors-watches', vendor: 'Michael Kors' }],
    ['orient', { url: '/collections/orient-watches', vendor: 'Orient' }],
    ['seiko', { url: '/collections/seiko-watches', vendor: 'Seiko' }],
    ['skagen', { url: '/collections/skagen-watches', vendor: 'Skagen' }],
    ['swatch', { url: '/collections/swatch-watches', vendor: 'Swatch' }],
    ['timex', { url: '/collections/timex-watches', vendor: 'Timex' }],
    ['tissot', { url: '/collections/tissot-watches', vendor: 'Tissot' }],
    ['tommy hilfiger', { url: '/collections/tommy-hilfiger-watches', vendor: 'Tommy Hilfiger' }],
  ]);

  const brandAliases = new Map([['tommy', 'tommy hilfiger']]);

  const navigationRoutes = new Map([
    ['all watches', '/collections/all-watches'],
    ['watches', '/collections/all-watches'],
    ['watch collection', '/collections/all-watches'],
    ['men', '/collections/mens-watches'],
    ['mens', '/collections/mens-watches'],
    ['men watch', '/collections/mens-watches'],
    ['men watches', '/collections/mens-watches'],
    ['mens watch', '/collections/mens-watches'],
    ['mens watches', '/collections/mens-watches'],
    ['hombre', '/collections/mens-watches'],
    ['relojes hombre', '/collections/mens-watches'],
    ['women', '/collections/womens-watches'],
    ['womens', '/collections/womens-watches'],
    ['women watch', '/collections/womens-watches'],
    ['women watches', '/collections/womens-watches'],
    ['womens watch', '/collections/womens-watches'],
    ['womens watches', '/collections/womens-watches'],
    ['mujer', '/collections/womens-watches'],
    ['relojes mujer', '/collections/womens-watches'],
    ['unisex', '/collections/unisex-watches'],
    ['unisex watch', '/collections/unisex-watches'],
    ['unisex watches', '/collections/unisex-watches'],
  ]);

  const intentCollections = new Map([
    ['automatic', automaticCollection],
    ['self winding', automaticCollection],
    ['mechanical', automaticCollection],
    ['quartz', '/collections/quartz-watches'],
    ['battery', '/collections/quartz-watches'],
    ['digital', '/collections/digital-watches'],
    ['chronograph', '/collections/chronograph-watches'],
    ['chrono', '/collections/chronograph-watches'],
    ['dive', '/collections/dive-watches'],
    ['diver', '/collections/dive-watches'],
    ['diving', '/collections/dive-watches'],
    ['sport', '/collections/sport-watches'],
    ['sports', '/collections/sport-watches'],
    ['casual', '/collections/casual-watches'],
    ['eco drive', '/collections/eco-drive-watches'],
    ['citizen eco drive', '/collections/eco-drive-watches'],
    ['sapphire', '/collections/sapphire-crystal-watches'],
    ['sapphire crystal', '/collections/sapphire-crystal-watches'],
    ['mineral', '/collections/mineral-crystal-watches'],
    ['mineral crystal', '/collections/mineral-crystal-watches'],
    ['hardlex', '/collections/hardlex-crystal-watches'],
    ['hardlex crystal', '/collections/hardlex-crystal-watches'],
  ]);

  const getBrand = (value) => {
    const brandKey = brandAliases.get(value) || value;
    return brandCollections.get(brandKey);
  };

  const automaticResult = (brand) => ({
    destination: `${automaticCollection}?filter.p.vendor=${encodeURIComponent(brand.vendor)}`,
    predictiveQuery: `vendor:"${brand.vendor}" title:automatic`,
  });

  const correctedCompoundRoutes = new Map([
    ['casio digital', '/collections/digital-watches?filter.p.vendor=Casio'],
    ['invicta pro diver', '/collections/dive-watches?filter.p.vendor=Invicta'],
    ['men dive', '/collections/dive-watches-for-men'],
    ['mens dive', '/collections/dive-watches-for-men'],
    ['men s dive', '/collections/dive-watches-for-men'],
  ]);

  const resolve = (value) => {
    const term = normalize(value);
    if (!term) return null;

    const coreTerm = stripWatchSuffix(term);
    const mensAutomatic = /^(?:men|mens|men s) automatic$/.test(coreTerm);
    const womensAutomatic = /^(?:women|womens|women s) automatic$/.test(coreTerm);

    if (mensAutomatic) {
      return {
        destination: '/collections/automatic-watches-for-men',
        predictiveQuery: 'tag:gender_men title:automatic',
      };
    }

    if (womensAutomatic) {
      return {
        destination: '/collections/automatic-watches-for-women',
        predictiveQuery: 'tag:gender_women title:automatic',
      };
    }

    if (coreTerm.endsWith(' automatic')) {
      const brand = getBrand(coreTerm.slice(0, -' automatic'.length));
      if (brand) return automaticResult(brand);
    }

    if (coreTerm.startsWith('automatic ')) {
      const brand = getBrand(coreTerm.slice('automatic '.length));
      if (brand) return automaticResult(brand);
    }

    const correctedCompoundDestination = correctedCompoundRoutes.get(coreTerm);
    if (correctedCompoundDestination) return { destination: correctedCompoundDestination };

    const navigationDestination = navigationRoutes.get(term);
    if (navigationDestination) return { destination: navigationDestination };

    const brand = getBrand(coreTerm);
    if (brand) return { destination: brand.url };

    const intentDestination = intentCollections.get(coreTerm);
    if (intentDestination) return { destination: intentDestination };

    return null;
  };

  return { resolve };
})();

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    const intent = MundoRelojeroSearchIntent.resolve(searchTerm);
    const predictiveQuery = intent?.predictiveQuery || searchTerm;
    const resourceTypes = intent?.predictiveQuery ? 'product' : 'product,collection,query';
    const searchableFields = intent?.predictiveQuery
      ? 'title,vendor,tag'
      : 'title,product_type,variants.title,vendor';
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], searchTerm, predictiveQuery);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(predictiveQuery)}&section_id=predictive-search&resources[type]=${resourceTypes}&resources[limit]=5&resources[limit_scope]=each&resources[options][unavailable_products]=hide&resources[options][fields]=${searchableFields}`, {

      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup, searchTerm, predictiveQuery);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup, displayTerm = this.searchTerm, requestTerm = displayTerm) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    if (requestTerm !== displayTerm) {
      this.predictiveSearchResults
        .querySelectorAll('[data-predictive-search-search-for-text], [data-predictive-search-live-region-count-value]')
        .forEach((element) => {
          element.textContent = element.textContent.replace(requestTerm, displayTerm);
        });
    }
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target.closest
      ? event.target.closest('form[role="search"], form[action*="/search"]')
      : null;
    if (!form) return;

    const input = form.querySelector('input[name="q"], input[type="search"]');
    const intent = MundoRelojeroSearchIntent.resolve(input?.value);
    if (!intent?.destination) return;

    event.preventDefault();
    window.location.assign(intent.destination);
  },
  true
);
